import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { EditOutlined, UploadOutlined, PlusOutlined } from "@ant-design/icons";
import { Form, Input, Select, InputNumber, Upload, Modal } from "antd";
import { UploadChangeParam } from "antd/lib/upload/interface";

import { selectPerfume } from "../../../redux-toolkit/perfume/perfume-selector";
import {
  selectIsAdminStateLoading,
  selectIsPerfumeEdited,
} from "../../../redux-toolkit/admin/admin-selector";
import { LoadingStatus } from "../../../types/types";
import { resetAdminState, setAdminLoadingState } from "../../../redux-toolkit/admin/admin-slice";
import { fetchPerfume } from "../../../redux-toolkit/perfume/perfume-thunks";
import { updatePerfume } from "../../../redux-toolkit/admin/admin-thunks";
import FinllieLoader from "../../logo/FinllieLoader";
import "./EditPerfume.css";

const { TextArea } = Input;
const { Option }   = Select;

/* ── Category map ── */
const CATEGORY_MAP: Record<string, string[]> = {
  "Packaged Drinking Water": ["250 ml Bottle","500 ml Bottle","1 Litre Bottle","2 Litre Bottle","5 Litre Can","20 Litre Jar"],
  "Premium Mineral Water":  ["Mineral Water","Glass Bottle Edition","Sparkling Water"],
  "Soft Drinks":            ["Lemon Soda","Cola","Orange","Jeera Soda","Limonata"],
};

const MAIN_CATEGORIES = Object.keys(CATEGORY_MAP).sort();

/* ── Bidirectional price helpers ── */
const calcFinalPrice = (actual: number, disc: number) =>
  actual > 0 ? Math.floor(actual - (actual * disc) / 100) : 0;
const calcDiscount = (actual: number, final: number) =>
  actual > 0 && final <= actual ? Math.round(((actual - final) / actual) * 100) : 0;

const EditPerfume: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const [form]   = Form.useForm();
  const params   = useParams<{ id: string }>();

  const perfumeData    = useSelector(selectPerfume);
  const isLoading      = useSelector(selectIsAdminStateLoading);
  const isPerfumeEdited = useSelector(selectIsPerfumeEdited);

  const [file,          setFile]          = useState<File | null>(null);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [resultModal,   setResultModal]   = useState<{ open: boolean; success: boolean; lines: string[] }>({
    open: false, success: false, lines: [],
  });

  /* ── Load product data ── */
  useEffect(() => {
    dispatch(setAdminLoadingState(LoadingStatus.LOADED));
    dispatch(fetchPerfume(params.id));
    return () => { dispatch(resetAdminState(LoadingStatus.LOADING)); };
  }, [dispatch, params.id]);

  /* ── Pre-fill form when data arrives ── */
  useEffect(() => {
    if (perfumeData) {
      form.setFieldsValue({
        ...perfumeData,
        actualPrice: perfumeData.actualPrice ?? perfumeData.price,
      });
      if (perfumeData.category && CATEGORY_MAP[perfumeData.category]) {
        setSubCategories([...CATEGORY_MAP[perfumeData.category]].sort());
      }
    }
  }, [perfumeData]);

  /* ── Success after update ── */
  useEffect(() => {
    if (isPerfumeEdited) {
      window.scrollTo(0, 0);
      setResultModal({ open: true, success: true, lines: ["Product details updated successfully!"] });
      dispatch(resetAdminState(LoadingStatus.SUCCESS));
    }
  }, [isPerfumeEdited]);

  /* ── Category change ── */
  const handleCategoryChange = (val: string) => {
    setSubCategories([...(CATEGORY_MAP[val] || [])].sort());
    form.setFieldsValue({ subCategory: undefined });
  };

  /* ── Bidirectional price handlers ── */
  const onActualPriceChange = (val: number | null) => {
    const actual = val ?? 0;
    const disc   = form.getFieldValue("discount") ?? 0;
    const dDisc  = form.getFieldValue("distributorDiscount") ?? 0;
    form.setFieldsValue({
      price:             calcFinalPrice(actual, disc),
      distributorPrice:  calcFinalPrice(actual, dDisc),
    });
  };

  const onRetailDiscountChange = (val: number | null) => {
    const disc   = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ price: calcFinalPrice(actual, disc) });
  };

  const onRetailPriceChange = (val: number | null) => {
    const final  = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ discount: calcDiscount(actual, final) });
  };

  const onDistDiscountChange = (val: number | null) => {
    const disc   = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ distributorPrice: calcFinalPrice(actual, disc) });
  };

  const onDistPriceChange = (val: number | null) => {
    const final  = val ?? 0;
    const actual = form.getFieldValue("actualPrice") ?? 0;
    form.setFieldsValue({ distributorDiscount: calcDiscount(actual, final) });
  };

  /* ── Submit ── */
  const onFormSubmit = (data: any): void => {
    const bodyFormData = new FormData();
    if (file) bodyFormData.append("file", file);
    const payload = { ...data, id: perfumeData?.id };
    bodyFormData.append("perfume", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    dispatch(updatePerfume(bodyFormData));
    // Success is handled via isPerfumeEdited selector above
    // Errors from redux are shown via the modal in the parent thunk
  };

  const handleUpload = ({ file }: UploadChangeParam<any>): void => setFile(file);

  return (
    <div className="ep-page">

      {/* Loader */}
      {isLoading && <FinllieLoader text="Updating product..." />}

      {/* Result Modal */}
      <Modal open={resultModal.open} footer={null} closable={false} centered width={400}>
        <div className="ep-modal-inner">
          <div className={`ep-modal-icon ${resultModal.success ? "success" : "error"}`}>
            {resultModal.success ? "✓" : "!"}
          </div>
          <h3 className="ep-modal-title">
            {resultModal.success ? "Product Updated!" : "Update Failed"}
          </h3>
          <div className="ep-modal-lines">
            {resultModal.lines.map((line, i) => (
              <p key={i} className="ep-modal-line">{line}</p>
            ))}
          </div>
          {!resultModal.success && (
            <p className="ep-modal-hint">Your form data is intact — fix the issue and try again.</p>
          )}
          <button
            className={`ep-modal-btn ${resultModal.success ? "success" : "error"}`}
            onClick={() => setResultModal({ open: false, success: false, lines: [] })}
          >
            {resultModal.success ? "Continue Editing" : "Close & Fix"}
          </button>
        </div>
      </Modal>

      {/* Page header */}
      <div className="ep-header">
        <EditOutlined className="ep-header-icon" />
        <span className="ep-header-title">Edit Product</span>
        {perfumeData?.id && (
          <span className="ep-header-id">ID #{perfumeData.id}</span>
        )}
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFormSubmit}
        className="ep-form"
        requiredMark={false}
      >
        <div className="ep-layout">

          {/* ══ LEFT COL ══ */}
          <div className="ep-col">

            {/* Basic info */}
            <div className="ep-card">
              <div className="ep-card-title">Product Information</div>

              <Form.Item label="Product Title" name="perfumeTitle" rules={[{ required: true, message: "Required" }]}>
                <Input size="large" placeholder="Product title" />
              </Form.Item>

              <Form.Item label="Brand" name="brand" rules={[{ required: true, message: "Required" }]}>
                <Input size="large" placeholder="Brand name" />
              </Form.Item>

              <div className="ep-row">
                <Form.Item label="Main Category" name="category" rules={[{ required: true, message: "Required" }]}>
                  <Select size="large" onChange={handleCategoryChange}>
                    {MAIN_CATEGORIES.map((c) => <Option key={c} value={c}>{c}</Option>)}
                  </Select>
                </Form.Item>

                <Form.Item label="Sub Category" name="subCategory" rules={[{ required: true, message: "Required" }]}>
                  <Select size="large" disabled={!subCategories.length}>
                    {subCategories.map((s) => <Option key={s} value={s}>{s}</Option>)}
                  </Select>
                </Form.Item>
              </div>

              <div className="ep-row">
                <Form.Item label="Pack Type" name="type">
                  <Select size="large" placeholder="Pack type">
                    {["250 ml Bottle","500 ml Bottle","1 Litre Bottle","2 Litre Bottle","5 Litre Can","20 Litre Can","25 Bottles (1L Each)"]
                      .map((t) => <Option key={t} value={t}>{t}</Option>)}
                  </Select>
                </Form.Item>

                <Form.Item label="Unit Type" name="unit">
                  <Input size="large" placeholder="e.g. pcs, ml" />
                </Form.Item>
              </div>

              <Form.Item label="Specifications" name="specifications">
                <TextArea rows={2} placeholder="Specs / ingredients" />
              </Form.Item>

              <Form.Item label="Product Description" name="description">
                <TextArea rows={3} placeholder="Detailed description" />
              </Form.Item>

              <Form.Item label="Tags" name="tags">
                <Input placeholder="e.g. mineral, sparkling (comma separated)" size="large" />
              </Form.Item>
            </div>

            {/* Inventory */}
            <div className="ep-card">
              <div className="ep-card-title">Inventory & Shipping</div>

              <div className="ep-row">
                <Form.Item label="Qty in Stock" name="quantity">
                  <InputNumber min={1} size="large" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label="Min Order Qty" name="minQuantity">
                  <InputNumber min={1} size="large" style={{ width: "100%" }} />
                </Form.Item>
              </div>

              <div className="ep-row">
                <Form.Item label="Max Order Qty" name="maxQuantity">
                  <InputNumber min={1} size="large" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label="Shipping Charges (%)" name="shipPer">
                  <InputNumber min={0} size="large" style={{ width: "100%" }} />
                </Form.Item>
              </div>

              <div className="ep-row">
                <Form.Item label="Delivery Timeline" name="deliveryTimeline">
                  <Select size="large" placeholder="Select timeline">
                    <Option value="1 Day">1 Day</Option>
                    <Option value="2 Days">2 Days</Option>
                    <Option value="5 Days">5 Days</Option>
                    <Option value="7 Days">7 Days</Option>
                    <Option value="15 Days">15 Days</Option>
                    <Option value="30 Days">30 Days</Option>
                  </Select>
                </Form.Item>

                <Form.Item label="Product Status" name="status">
                  <Select size="large" placeholder="Select status">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                    <Option value="draft">Draft</Option>
                  </Select>
                </Form.Item>
              </div>

              <Form.Item label="Seller Name" name="sellerName">
                <Input size="large" placeholder="Seller name (optional)" />
              </Form.Item>
            </div>
          </div>

          {/* ══ RIGHT COL ══ */}
          <div className="ep-col">

            {/* Retail Pricing */}
            <div className="ep-card">
              <div className="ep-card-title">
                Retail Pricing
                <span className="ep-card-badge retail">Customer</span>
              </div>
              <p className="ep-card-hint">Change any field — the other updates automatically.</p>

              <Form.Item label="Actual Price / MRP (₹)" name="actualPrice">
                <InputNumber min={0} size="large" style={{ width: "100%" }} placeholder="Original MRP" onChange={onActualPriceChange} />
              </Form.Item>

              <div className="ep-price-row">
                <Form.Item label="Retail Discount (%)" name="discount">
                  <InputNumber min={0} max={100} size="large" style={{ width: "100%" }} addonAfter="%" onChange={onRetailDiscountChange} />
                </Form.Item>
                <div className="ep-arrow">→</div>
                <Form.Item label="Final Retail Price (₹)" name="price">
                  <InputNumber min={0} size="large" style={{ width: "100%" }} onChange={onRetailPriceChange} />
                </Form.Item>
              </div>

              <div className="ep-calc-note">
                Change <strong>Discount %</strong> → Price updates &nbsp;|&nbsp;
                Change <strong>Price</strong> → Discount % updates
              </div>
            </div>

            {/* Distributor Pricing */}
            <div className="ep-card">
              <div className="ep-card-title">
                Distributor Pricing
                <span className="ep-card-badge dist">Distributor</span>
              </div>
              <p className="ep-card-hint">Based on the same Actual Price / MRP above.</p>

              <div className="ep-price-row">
                <Form.Item label="Distributor Discount (%)" name="distributorDiscount">
                  <InputNumber min={0} max={100} size="large" style={{ width: "100%" }} addonAfter="%" onChange={onDistDiscountChange} />
                </Form.Item>
                <div className="ep-arrow">→</div>
                <Form.Item label="Distributor Price (₹)" name="distributorPrice">
                  <InputNumber min={0} size="large" style={{ width: "100%" }} onChange={onDistPriceChange} />
                </Form.Item>
              </div>

              <div className="ep-calc-note">
                Change <strong>Discount %</strong> → Price updates &nbsp;|&nbsp;
                Change <strong>Price</strong> → Discount % updates
              </div>
            </div>

            {/* Image */}
            <div className="ep-card">
              <div className="ep-card-title">Product Image</div>

              {/* Current image preview */}
              {perfumeData?.filename && !file && (
                <div className="ep-img-preview">
                  <img src={perfumeData.filename} alt={perfumeData.perfumeTitle} className="ep-img" />
                  <span className="ep-img-label">Current image</span>
                </div>
              )}

              {/* New file selected preview */}
              {file && (
                <div className="ep-img-preview">
                  <img src={URL.createObjectURL(file)} alt="New upload" className="ep-img" />
                  <span className="ep-img-label new">New image selected</span>
                </div>
              )}

              <Upload
                beforeUpload={() => false}
                onChange={handleUpload}
                maxCount={1}
                accept="image/*"
                showUploadList={false}
              >
                <button type="button" className="ep-upload-btn">
                  <UploadOutlined />
                  {file ? " Change Image" : " Replace Image"}
                </button>
              </Upload>

              {file && (
                <div className="ep-file-selected">
                  ✓ {file.name}
                  <button type="button" className="ep-file-remove" onClick={() => setFile(null)}>✕</button>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`ep-submit-btn ${isLoading ? "loading" : ""}`}
              disabled={isLoading}
            >
              {isLoading ? "Updating Product..." : "Update Product"}
            </button>
          </div>

        </div>
      </Form>
    </div>
  );
};

export default EditPerfume;